import React, { useState } from 'react';
import { 
  Building2, Home, FileText, DollarSign, Trash2, Plus, 
  Zap, Droplet, Trash, Wifi, Snowflake, AlertCircle,
  ChevronDown, ChevronUp, Edit2, Check, X
} from 'lucide-react';

const LeaseTypeManager = ({ leaseType, setLeaseType, formData, setFormData }) => {
  const [showServicesPanel, setShowServicesPanel] = useState(false);
  const [newService, setNewService] = useState({ name: '', amount: '', frequency: 'monthly' });
  const [showNewService, setShowNewService] = useState(false);

  // Services disponibles
  const availableServices = [
    { id: 'water', label: 'Water', icon: Droplet, defaultPrice: 75 },
    { id: 'electricity', label: 'Electricity', icon: Zap, defaultPrice: 150 },
    { id: 'trash', label: 'Trash Collection', icon: Trash, defaultPrice: 50 },
    { id: 'wifi', label: 'WiFi / Internet', icon: Wifi, defaultPrice: 89 },
    { id: 'hvac', label: 'HVAC / Climate Control', icon: Snowflake, defaultPrice: 200 },
    { id: 'security', label: 'Security Monitoring', icon: AlertCircle, defaultPrice: 120 },
  ];

  const leaseTypes = [
    { 
      id: 'active', 
      label: 'Active Lease', 
      icon: Building2, 
      color: 'blue',
      description: 'Airport owns the hangar',
      fields: ['monthly_rent']
    },
    { 
      id: 'ground', 
      label: 'Ground Lease', 
      icon: Home, 
      color: 'green',
      description: 'Airport owns land, client owns hangar',
      fields: ['ground_rent']
    },
    { 
      id: 'services_only', 
      label: 'Services Only', 
      icon: FileText, 
      color: 'purple',
      description: 'Client owns land + hangar, only services billed',
      fields: ['service_fees']
    }
  ];

  const handleServiceToggle = (serviceId) => {
    const currentIncluded = formData.included_services || [];
    if (currentIncluded.includes(serviceId)) {
      setFormData({
        ...formData,
        included_services: currentIncluded.filter(s => s !== serviceId)
      });
    } else {
      setFormData({
        ...formData,
        included_services: [...currentIncluded, serviceId]
      });
    }
  };

  const handleAddCustomService = () => {
    if (newService.name && newService.amount) {
      const currentFees = formData.service_fees || [];
      setFormData({
        ...formData,
        service_fees: [...currentFees, {
          id: Date.now(),
          name: newService.name,
          amount: parseFloat(newService.amount),
          frequency: newService.frequency
        }]
      });
      setNewService({ name: '', amount: '', frequency: 'monthly' });
      setShowNewService(false);
    }
  };

  const handleRemoveCustomService = (serviceId) => {
    setFormData({
      ...formData,
      service_fees: (formData.service_fees || []).filter(s => s.id !== serviceId)
    });
  };

  const currentType = leaseTypes.find(t => t.id === leaseType);

  return (
    <div className="space-y-4">
      {/* Lease Type Selection */}
      <div>
        <label className="block text-gray-400 text-xs mb-2 font-medium">
          Contract Type
        </label>
        <div className="grid grid-cols-3 gap-3">
          {leaseTypes.map((type) => {
            const Icon = type.icon;
            const isSelected = leaseType === type.id;
            return (
              <button
                key={type.id}
                type="button"
                onClick={() => setLeaseType(type.id)}
                className={`p-3 rounded-xl border-2 transition-all text-left ${
                  isSelected 
                    ? `border-${type.color}-500 bg-${type.color}-500/10`
                    : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={`w-4 h-4 ${isSelected ? `text-${type.color}-400` : 'text-gray-500'}`} />
                  <span className={`text-sm font-medium ${isSelected ? `text-${type.color}-400` : 'text-gray-300'}`}>
                    {type.label}
                  </span>
                </div>
                <p className="text-xs text-gray-500">{type.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Dynamic fields based on lease type */}
      {leaseType === 'active' && (
        <div>
          <label className="block text-gray-400 text-xs mb-1 font-medium">
            Monthly Rent ($)
          </label>
          <input
            type="number"
            step="0.01"
            value={formData.monthly_rent || ''}
            onChange={(e) => setFormData({ ...formData, monthly_rent: parseFloat(e.target.value) || 0 })}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
            placeholder="Enter monthly rent amount"
          />
        </div>
      )}

      {leaseType === 'ground' && (
        <div>
          <label className="block text-gray-400 text-xs mb-1 font-medium">
            Ground Rent ($/month)
          </label>
          <input
            type="number"
            step="0.01"
            value={formData.ground_rent || ''}
            onChange={(e) => setFormData({ ...formData, ground_rent: parseFloat(e.target.value) || 0 })}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
            placeholder="Enter ground rent amount"
          />
          <p className="text-xs text-gray-500 mt-1">
            Client owns the hangar structure, airport owns the land
          </p>
        </div>
      )}

      {/* Services Section - Common for all lease types */}
      <div className="border-t border-gray-700 pt-4 mt-4">
        <button
          type="button"
          onClick={() => setShowServicesPanel(!showServicesPanel)}
          className="flex items-center justify-between w-full text-left"
        >
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-medium text-gray-300">Services & Extra Charges</span>
          </div>
          {showServicesPanel ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
        </button>

        {showServicesPanel && (
          <div className="mt-3 space-y-4">
            {/* Included in Rent */}
            <div>
              <label className="block text-gray-400 text-xs mb-2">
                Services included in rent
              </label>
              <div className="flex flex-wrap gap-2">
                {availableServices.map((service) => {
                  const Icon = service.icon;
                  const isIncluded = (formData.included_services || []).includes(service.id);
                  return (
                    <button
                      key={service.id}
                      type="button"
                      onClick={() => handleServiceToggle(service.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition ${
                        isIncluded
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-gray-700/50 text-gray-400 border border-gray-600 hover:bg-gray-700'
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      {service.label}
                      {isIncluded && <Check className="w-3 h-3 ml-1" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Extra Service Fees */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-gray-400 text-xs">
                  Extra service fees (billed separately)
                </label>
                <button
                  type="button"
                  onClick={() => setShowNewService(!showNewService)}
                  className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Add fee
                </button>
              </div>

              {showNewService && (
                <div className="bg-gray-700/50 rounded-lg p-3 mb-3 space-y-2">
                  <input
                    type="text"
                    placeholder="Service name (e.g., Snow Removal)"
                    value={newService.name}
                    onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-600 rounded-lg p-2 text-white text-sm"
                  />
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Amount ($)"
                      value={newService.amount}
                      onChange={(e) => setNewService({ ...newService, amount: e.target.value })}
                      className="flex-1 bg-gray-800 border border-gray-600 rounded-lg p-2 text-white text-sm"
                    />
                    <select
                      value={newService.frequency}
                      onChange={(e) => setNewService({ ...newService, frequency: e.target.value })}
                      className="flex-1 bg-gray-800 border border-gray-600 rounded-lg p-2 text-white text-sm"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                      <option value="one-time">One-time</option>
                    </select>
                    <button
                      type="button"
                      onClick={handleAddCustomService}
                      className="px-3 bg-green-600 hover:bg-green-700 rounded-lg text-white"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {(formData.service_fees || []).length > 0 && (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {formData.service_fees.map((fee) => (
                    <div key={fee.id} className="flex items-center justify-between p-2 bg-gray-700/30 rounded-lg">
                      <div>
                        <span className="text-white text-sm">{fee.name}</span>
                        <span className="text-gray-400 text-xs ml-2">
                          ${fee.amount} / {fee.frequency}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveCustomService(fee.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Billing Frequency */}
            <div>
              <label className="block text-gray-400 text-xs mb-1">
                Billing Frequency
              </label>
              <select
                value={formData.billing_frequency || 'monthly'}
                onChange={(e) => setFormData({ ...formData, billing_frequency: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Summary of charges */}
      {(leaseType === 'active' || leaseType === 'ground') && (
        <div className="bg-gray-700/30 rounded-lg p-3">
          <p className="text-xs text-gray-400 mb-1">Monthly Summary:</p>
          <p className="text-white font-semibold">
            ${leaseType === 'active' 
              ? (formData.monthly_rent || 0).toLocaleString() 
              : (formData.ground_rent || 0).toLocaleString()}
            {leaseType === 'active' ? ' (Rent)' : ' (Ground Rent)'}
          </p>
          {(formData.service_fees || []).filter(f => f.frequency === 'monthly').length > 0 && (
            <p className="text-xs text-gray-400 mt-1">
              + {formData.service_fees.filter(f => f.frequency === 'monthly').length} service fees
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default LeaseTypeManager;